/**
 * Mailbox Seam types (ENG-E23) — Machine, Job, JobTurn
 *
 * Persistence contracts for a pull-based job mailbox: the browser drops jobs
 * into a per-machine queue; the employee's daemon pulls them. These types are
 * the single source of truth shared across api / mcp / cli / web.
 *
 * The string-literal unions below mirror the enum-like String fields in the
 * Prisma schemas (schema.prisma + schema.postgres.prisma) EXACTLY. Enums are
 * modeled as String in Prisma (SQLite has no native enums) with application-
 * level validation; these const arrays + unions provide that validation layer.
 *
 * Date fields are typed as `string` (ISO 8601) to match the JSON-serialized
 * shape returned by the REST API, consistent with the existing `User` type.
 */
/**
 * Machine lifecycle status. Mirrors `Machine.status` (default: "active").
 */
export const MACHINE_STATUSES = ["active", "inactive", "revoked"];
/**
 * Job type — what kind of work the queued job represents.
 * Mirrors `Job.type`.
 */
export const JOB_TYPES = [
    "epic_request",
    "interview_reply",
    "start_execution",
    "connect_repo",
];
/**
 * Job status — drives the queue state machine. Mirrors `Job.status`
 * (default: "pending").
 *
 * Cancel (ENG-299): a pending job goes straight to "cancelled"; an active job
 * (claimed / running / awaiting_input) goes to "cancelling" until the runner
 * stops it and reports back, at which point it lands on "cancelled".
 *
 * Waiting (ENG-330): a "waiting" job is parked until its `resumeAt` time,
 * after which it becomes claimable again.
 */
export const JOB_STATUSES = [
    "pending",
    "claimed",
    "running",
    "awaiting_input",
    "waiting",
    "cancelling",
    "done",
    "failed",
    "cancelled",
];
/**
 * Terminal job states — no further turns or transitions. Single source of
 * truth shared by api (queue state machine) and web (UI badges / buttons).
 */
export const TERMINAL_JOB_STATUSES = ["done", "failed", "cancelled"];
/** Runtime check: is `status` a terminal job state? */
export function isTerminalJobStatus(status) {
    return TERMINAL_JOB_STATUSES.includes(status);
}
/**
 * Turn role — who authored a turn in a job's conversation log.
 * Mirrors `JobTurn.role`.
 */
export const TURN_ROLES = ["agent", "user"];
/**
 * ENG-395: how often a build runs its review / QA / security checks.
 *  - "every" — after every phase (most thorough, slowest)
 *  - "smart" — per-phase only for risky phases, plus a final pass (default)
 *  - "end"   — once, at the end of the build (fastest)
 */
export const CHECK_MODES = ["every", "smart", "end"];
/** The `kind` discriminant values for {@link StartExecutionPayload}. */
export const START_EXECUTION_KINDS = ["plan", "orchestrate", "refine", "follow_up"];
/**
 * Lowest CLI version that resumes a replied-to job in the same agent session
 * (ENG-339). Older CLIs ignore the claim's `resume` block and run fresh.
 */
export const MIN_CLI_FOR_REPLY_RESUME = "1.21.10";
/** Runtime type guard for {@link MachineStatus}. */
export function isMachineStatus(value) {
    return MACHINE_STATUSES.includes(value);
}
/** Runtime type guard for {@link JobType}. */
export function isJobType(value) {
    return JOB_TYPES.includes(value);
}
/** Runtime type guard for {@link JobStatus}. */
export function isJobStatus(value) {
    return JOB_STATUSES.includes(value);
}
/** Runtime type guard for {@link TurnRole}. */
export function isTurnRole(value) {
    return TURN_ROLES.includes(value);
}
/**
 * Canonicalize a git remote URL into a single portable project identity
 * (ENG-175). This is a **pure** function shared by the server (to store
 * `Team.gitRemote`) and the daemon (to compare against its serviced remotes),
 * so both sides derive the SAME string for the same repository regardless of
 * which transport form the user pasted.
 *
 * The canonical form is always `https://<host>/<owner>/<repo>`:
 *  - the host is lowercased;
 *  - `git@host:owner/repo(.git)` (scp-like) and
 *    `ssh://[git@]host[:port]/owner/repo(.git)` are rewritten to https;
 *  - any userinfo (`git@`) is dropped;
 *  - any port is dropped (so `:22` and no-port ssh URLs match);
 *  - a trailing `.git` and a single trailing slash are stripped.
 *
 * The path (owner/repo) case is preserved — only the host is lowercased.
 * Empty / unparseable input returns the trimmed input unchanged.
 *
 * @example
 * normalizeGitRemote("git@github.com:Owner/Repo.git")
 *   // → "https://github.com/Owner/Repo"
 * normalizeGitRemote("ssh://git@github.com:22/Owner/Repo/")
 *   // → "https://github.com/Owner/Repo"
 * normalizeGitRemote("HTTPS://GitHub.com/Owner/Repo")
 *   // → "https://github.com/Owner/Repo"
 */
export function normalizeGitRemote(raw) {
    // Typed `string`, but a JS caller can still pass a runtime null/undefined —
    // the `string` type is not a runtime guarantee. Guard defensively (covered by
    // the empty/whitespace test, which passes `undefined` via @ts-expect-error).
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- intentional runtime null-safety
    const input = (raw ?? "").trim();
    if (input === "")
        return "";
    let host = "";
    let path = "";
    // A URL with an explicit scheme: scheme://[user@]host[:port]/path
    const hasScheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(input);
    if (hasScheme) {
        let url;
        try {
            url = new URL(input);
        }
        catch {
            return input;
        }
        // `hostname` excludes userinfo AND the port by definition.
        host = url.hostname.toLowerCase();
        path = url.pathname;
    }
    else {
        // scp-like syntax: [user@]host:path (host cannot contain a colon).
        const scp = /^(?:[^@]+@)?([^:]+):(.*)$/.exec(input);
        const scpHost = scp?.[1];
        const scpPath = scp?.[2];
        if (scpHost === undefined || scpPath === undefined) {
            // Unknown shape — best-effort tidy of trailing slash / .git only.
            return input.replace(/\/+$/, "").replace(/\.git$/, "");
        }
        host = scpHost.toLowerCase();
        path = scpPath;
    }
    // Collapse the path to a single leading slash, drop trailing slashes, drop .git
    path = path.replace(/^\/+/, "").replace(/\/+$/, "").replace(/\.git$/, "");
    return `https://${host}/${path}`;
}
//# sourceMappingURL=mailbox.js.map